import { VoiceRecordingBar } from '@euler/components/VoiceRecordingBar';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useVoiceDictation } from '@euler/functions/useVoiceDictation';
import { ArrowUp, Check, Mic, Square, X } from 'lucide-react-native';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useTheme } from 'styled-components/native';
import {
  useAIAssistantActions,
  useAIAssistantState,
} from '../AIAssistantProvider';
import { useStyles } from './styles';

export const InputBar = memo(function InputBar() {
  const theme = useTheme();
  const { rpx } = useResponsiveContext();
  const actions = useAIAssistantActions();
  const state = useAIAssistantState();
  const styles = useStyles();
  const [text, setText] = useState('');
  const inputRef = useRef<TextInput>(null);
  const isWeb = Platform.OS === 'web';
  const baseHeightRef = useRef(0);
  const minH = isWeb ? 40 : rpx(44);
  const maxH = isWeb ? 120 : rpx(120);
  const [inputHeight, setInputHeight] = useState(minH);

  useEffect(() => {
    if (!isWeb || !inputRef.current) return;
    const el = inputRef.current as unknown as HTMLTextAreaElement;
    baseHeightRef.current = el.scrollHeight;
    setInputHeight(baseHeightRef.current);
  }, [isWeb]);

  const { composerFocus$ } = actions;
  useEffect(() => {
    const subscription = composerFocus$.subscribe(
      () => inputRef.current?.focus(),
    );
    return () => subscription.unsubscribe();
  }, [composerFocus$]);

  const handleChangeText = useCallback(
    (val: string) => {
      setText(val);
      if (!isWeb || !inputRef.current) return;
      const el = inputRef.current as unknown as HTMLTextAreaElement;
      const base = baseHeightRef.current;
      el.style.height = base + 'px';
      const h = el.scrollHeight > base ? Math.min(el.scrollHeight, maxH) : base;
      el.style.height = h + 'px';
      setInputHeight(h);
    },
    [isWeb, maxH],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || state.isStreaming) return;
    actions.sendMessage(trimmed);
    setText('');
    const base = baseHeightRef.current || minH;
    setInputHeight(base);
    if (isWeb && inputRef.current) {
      (inputRef.current as unknown as HTMLTextAreaElement).style.height =
        base + 'px';
    }
  }, [text, state.isStreaming, actions, minH, isWeb]);

  // Dictation appends to the draft rather than sending, so a transcript can
  // still be corrected before it goes.
  const appendRecognized = useCallback(
    (recognized: string) =>
      setText(prev => (prev ? `${prev}${recognized}` : recognized)),
    [],
  );
  const dictation = useVoiceDictation({ onText: appendRecognized });

  const accentColor = theme.link;
  const hasText = text.trim().length > 0;
  const canSend = hasText && !state.isStreaming;
  const btnSize = isWeb ? baseHeightRef.current || 40 : rpx(40);
  const roundBtn = (backgroundColor: string) => [
    styles.actionBtn,
    {
      width: btnSize,
      height: btnSize,
      borderRadius: btnSize / 2,
      backgroundColor,
    },
  ];

  // Recording replaces the field with the waveform, flanked by discard /
  // accept — the same shape the chat composer uses.
  if (dictation.recording) {
    return (
      <View style={styles.inputContainer}>
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={dictation.cancel}
          style={roundBtn('#E2E8F0')}
        >
          <X color="#475569" size={rpx(20)} strokeWidth={2.5} />
        </TouchableOpacity>
        <VoiceRecordingBar meter$={dictation.meter$} />
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={dictation.complete}
          style={roundBtn(accentColor)}
        >
          <Check color="#fff" size={rpx(20)} strokeWidth={2.5} />
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <View style={styles.inputContainer}>
      <View
        style={[
          styles.inputFieldWrapper,
          state.isStreaming && styles.inputFieldDisabled,
        ]}
      >
        <TextInput
          ref={inputRef}
          value={text}
          onChangeText={handleChangeText}
          placeholder={state.isStreaming ? '等待回复中...' : '输入消息...'}
          placeholderTextColor="#94A3B8"
          multiline
          style={[
            styles.inputField,
            { height: Math.max(minH, Math.min(inputHeight, maxH)) },
            isWeb && ({ resize: 'none', overflow: 'hidden' } as any),
          ]}
          {...(!isWeb && {
            onContentSizeChange: (e: any) => {
              const h = e.nativeEvent.contentSize.height + rpx(20);
              setInputHeight(Math.min(h, maxH));
            },
          })}
          returnKeyType={Platform.OS === 'ios' ? 'default' : 'send'}
          blurOnSubmit={false}
          editable={!state.isStreaming}
        />
      </View>
      {state.isStreaming ? (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={actions.stopStreaming}
          style={[
            styles.actionBtn,
            {
              width: btnSize,
              height: btnSize,
              borderRadius: btnSize / 2,
              backgroundColor: '#EF4444',
            },
          ]}
        >
          <Square color="#fff" size={rpx(14)} fill="#fff" />
        </TouchableOpacity>
      ) : hasText ? (
        <TouchableOpacity
          activeOpacity={canSend ? 0.7 : 1}
          onPress={handleSubmit}
          disabled={!canSend}
          style={[
            styles.actionBtn,
            {
              width: btnSize,
              height: btnSize,
              borderRadius: btnSize / 2,
              backgroundColor: canSend ? accentColor : '#E2E8F0',
            },
          ]}
        >
          <ArrowUp
            color={canSend ? '#fff' : '#94A3B8'}
            size={rpx(20)}
            strokeWidth={2.5}
          />
        </TouchableOpacity>
      ) : dictation.supported ? (
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={dictation.start}
          disabled={dictation.recognizing}
          style={roundBtn('#E2E8F0')}
        >
          {dictation.recognizing ? (
            <ActivityIndicator size="small" color="#64748B" />
          ) : (
            <Mic color="#64748B" size={rpx(20)} strokeWidth={2} />
          )}
        </TouchableOpacity>
      ) : null}
    </View>
  );
});
